import { useCallback, useRef } from 'react';
import type {
  AuthStatusResponse,
  BriefInput,
  ExtractionResult,
  ValidationIssue,
} from '../types';
import { Button, Card, Notice, cx } from '../components/ui/primitives';
import { BriefForm, type BriefFormHandle } from './BriefInputScreen';

/**
 * Start page.
 *
 * Combines Google sign-in with the audience push details in one screen, so a
 * user lands, signs in, pastes, and is taken straight to the form.
 *
 * The paste area is usable before signing in: whatever is typed is preserved
 * across the sign-in redirect, so nobody is forced to authenticate before they
 * can start working.
 */

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  access_denied: 'Google sign-in was cancelled. Try again to continue.',
  state_mismatch: 'The sign-in attempt could not be verified. Please try again.',
  token_exchange_failed: 'Google could not complete the sign-in. Please try again.',
  missing_code: 'Google did not return a sign-in code. Please try again.',
  no_email: 'Google did not share an email address for that account.',
  not_authorized: 'That Google account is not permitted to use this application.',
};

/** Google's four-colour mark, inlined so it needs no network request. */
function GoogleMark() {
  return (
    <svg viewBox="0 0 18 18" aria-hidden="true" className="h-4 w-4">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26c-.81.54-1.84.86-3.05.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.34A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72a5.41 5.41 0 0 1 0-3.44V4.94H.96a9 9 0 0 0 0 8.12l3.01-2.34Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.94l3.01 2.34C4.68 5.16 6.66 3.58 9 3.58Z"
      />
    </svg>
  );
}

function SignInPanel({
  auth,
  authError,
  authErrorEmail,
  onConnect,
  onSignOut,
}: {
  auth: AuthStatusResponse | null;
  authError: string | null;
  authErrorEmail: string | null;
  onConnect: () => void;
  onSignOut: () => void;
}) {
  const connected = Boolean(auth?.connected);
  const authorized = Boolean(auth?.authorized);
  const message = authError
    ? (AUTH_ERROR_MESSAGES[authError] ?? 'Google sign-in did not complete. Please try again.')
    : null;

  return (
    <Card className="p-5 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span
            aria-hidden="true"
            className={cx(
              'mt-1 h-2.5 w-2.5 shrink-0 rounded-full',
              connected && authorized ? 'bg-success' : connected ? 'bg-danger' : 'bg-ink-disabled',
            )}
          />
          <div className="min-w-0">
            <p className="text-section font-semibold text-ink">
              {connected
                ? authorized
                  ? 'Google Drive Connected'
                  : 'Account not permitted'
                : 'Step 1 — Sign in with Google'}
            </p>
            <p className="mt-0.5 text-micro text-ink-muted">
              {connected ? (
                <>
                  Signed in as <span className="text-ink">{auth?.email}</span>
                  {authorized
                    ? '. Rollout documents will be created in your Drive folders.'
                    : '. Ask an administrator to add this account to the allowlist.'}
                </>
              ) : (
                'Needed to create and update rollout documents in Google Drive. Anything you paste below is kept while you sign in.'
              )}
            </p>
          </div>
        </div>

        {connected ? (
          <Button variant="secondary" onClick={onSignOut} className="shrink-0 px-4 py-2">
            Sign out
          </Button>
        ) : (
          <Button variant="secondary" onClick={onConnect} className="shrink-0 gap-2.5 px-4 py-2">
            <GoogleMark />
            Sign in with Google
          </Button>
        )}
      </div>

      {message ? (
        <div className="mt-4">
          <Notice tone="error" title="Sign-in problem">
            {message}
            {authErrorEmail ? (
              <span className="mt-1 block text-ink-faint">Account: {authErrorEmail}</span>
            ) : null}
          </Notice>
        </div>
      ) : null}
    </Card>
  );
}

export function StartScreen({
  initial,
  auth,
  authError,
  authErrorEmail,
  serverIssues,
  submitting,
  onConnect,
  onSignOut,
  onContinue,
}: {
  initial: BriefInput;
  auth: AuthStatusResponse | null;
  authError: string | null;
  authErrorEmail: string | null;
  serverIssues: ValidationIssue[];
  submitting: boolean;
  onConnect: () => void;
  onSignOut: () => void;
  onContinue: (input: BriefInput, attachments: ExtractionResult[]) => void;
}) {
  /**
   * The form publishes a handle rather than lifting its state up. Storing it in
   * a ref keeps `onReady` stable, so publishing it cannot trigger a re-render
   * loop between the two components.
   */
  const form = useRef<BriefFormHandle | null>(null);
  const handleFormReady = useCallback((handle: BriefFormHandle) => {
    form.current = handle;
  }, []);

  const ready = Boolean(auth?.connected && auth.authorized);

  const handleContinue = () => {
    const handle = form.current;
    if (!handle) return;
    // Reveals inline errors and tells us whether it is safe to proceed.
    if (!handle.attemptContinue()) return;
    onContinue(handle.input, handle.attachments);
  };

  return (
    <div className="space-y-5">
      <SignInPanel
        auth={auth}
        authError={authError}
        authErrorEmail={authErrorEmail}
        onConnect={onConnect}
        onSignOut={onSignOut}
      />

      <BriefForm initial={initial} serverIssues={serverIssues} onReady={handleFormReady} />

      <div className="flex flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-end">
        {!ready ? (
          <p className="text-micro text-ink-muted sm:mr-auto">
            Sign in with Google to continue. Your pasted details are kept.
          </p>
        ) : null}

        <Button variant="secondary" onClick={() => form.current?.clear()}>
          Clear
        </Button>

        {/*
          The primary action follows the blocking requirement: sign in first,
          then continue. Continue is never shown as available while it would
          fail at the next step for want of Drive access.
        */}
        {ready ? (
          <Button onClick={handleContinue} loading={submitting}>
            Continue
          </Button>
        ) : (
          <Button onClick={onConnect} className="gap-2.5">
            <GoogleMark />
            Sign in with Google
          </Button>
        )}
      </div>
    </div>
  );
}
